import { Waveform } from '@plato/ui/primitives/waveform';
import { captureException } from '@sentry/react';
import { XIcon } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Button } from '../../../components/ui/button.tsx';
import { Text } from '../../../components/ui/text.tsx';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useVoiceRecording } from '../../../lib/contexts/voice-recording-context.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { cn } from '../../../lib/utils.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { VoiceInputPayload } from '../../analytics/types/analytics-events.ts';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import { Dialog } from '../../dialog/dialog.tsx';
import type { ModalInnerProps } from '../../modal/index.tsx';
import { useDeepgram } from '../hooks/use-deepgram.ts';
import { useMicrophone } from '../hooks/use-microphone.ts';

export type RecordingDialogProps = {
    trackingContext: VoiceInputPayload;
    onTranscribed: (text: string) => void;
} & ModalInnerProps;

const formatElapsed = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    return `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;
};

export const RecordingDialog = DialogStateManager.create<RecordingDialogProps>(
    ({ trackingContext, onTranscribed }): ReactNode => {
        const { visible, remove } = useDialog();
        const sessionId = useId();
        const { t, language } = useLocale();
        const { toast } = useToast();
        const { currentSessionId, startSession, endSession } = useVoiceRecording();
        const sendEvent = useAnalytics();

        const isCanceledRef = useRef(false);
        const hasStartedRef = useRef(false);

        const { isTranscribing, transcribe } = useDeepgram((text) => {
            endSession(sessionId);

            if (!isCanceledRef.current && text) {
                onTranscribed(text);
                sendEvent('voice_transcription_completed', {
                    transcript_text: text,
                    character_count: text.length,
                    language,
                    ...trackingContext,
                });
            }

            remove();
        });

        const { isRecording, startRecording, stopRecording } = useMicrophone((audioBlob, duration) => {
            if (isCanceledRef.current) {
                endSession(sessionId);
                remove();
                return;
            }
            transcribe({ blob: audioBlob, duration });
        });

        const startedAtRef = useRef(performance.now());
        const [elapsedMs, setElapsedMs] = useState(0);
        const [timerRunning, setTimerRunning] = useState(true);

        useEffect(() => {
            if (!timerRunning) {
                return;
            }

            const id = setInterval(() => {
                setElapsedMs(performance.now() - startedAtRef.current);
            }, 250);

            return () => {
                clearInterval(id);
            };
        }, [timerRunning]);

        useEffect(() => {
            if (hasStartedRef.current) {
                return;
            }
            hasStartedRef.current = true;

            // Another surface (e.g. the toolbar mic) is already recording — refuse.
            if (currentSessionId) {
                remove();
                return;
            }

            startSession(sessionId);

            void (async () => {
                try {
                    await startRecording();
                    // Only after the mic is live — firing on mount would count
                    // dialog opens (incl. denied mic permission) as recordings.
                    sendEvent('voice_recording_started', trackingContext);
                } catch (error) {
                    captureException(error);
                    endSession(sessionId);
                    toast({
                        icon: 'warning',
                        title: t('voice__mic_error_title', { ns: 'common' }),
                        description: t('voice__mic_error_description', { ns: 'common' }),
                    });
                    remove();
                }
            })();
            // Mount-only: startSession / endSession, translations, etc. are stable
            // enough that a re-run would double-start recording. hasStartedRef guards
            // Strict-Mode's double-invoke.
            // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment
        }, []);

        const cancel = (): void => {
            if (!isRecording || isCanceledRef.current) {
                return;
            }
            isCanceledRef.current = true;
            setTimerRunning(false);
            stopRecording();
        };

        const finish = (): void => {
            if (!isRecording || isCanceledRef.current) {
                return;
            }
            setTimerRunning(false);
            stopRecording();
        };

        const processing = !isRecording || isTranscribing;
        const elapsedLabel = formatElapsed(elapsedMs);

        return (
            <Dialog open={visible} onClose={cancel}>
                <div className="flex w-screen max-w-md flex-col gap-5 px-5 py-5 sm:px-6">
                    <div className="flex items-center justify-between gap-3">
                        <Text size="lg" weight="medium" color="neutral-900">
                            {t('voice__recording_title', { ns: 'common' })}
                        </Text>
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1.5">
                                <span
                                    className={cn(
                                        'h-2 w-2 animate-pulse rounded-full',
                                        processing ? 'bg-neutral-400' : 'bg-blue-500',
                                    )}
                                />
                                <Text size="sm" color="neutral-500" className="tabular-nums">
                                    {elapsedLabel}
                                </Text>
                            </div>
                            <button
                                type="button"
                                aria-label={t('form__cancel', { ns: 'common' })}
                                onClick={cancel}
                                className="rounded-md p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
                            >
                                <XIcon className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                    {processing ? (
                        <div className="flex h-16 items-center justify-center">
                            <Text size="sm" color="neutral-500" className="animate-pulse">
                                {t('voice__processing', { ns: 'common' })}
                            </Text>
                        </div>
                    ) : (
                        <Waveform />
                    )}
                    <div className="flex justify-end gap-2">
                        <Button
                            variant="subtle"
                            disabled={processing}
                            className="bg-neutral-100 hover:bg-neutral-200"
                            onClick={cancel}
                        >
                            {t('form__cancel', { ns: 'common' })}
                        </Button>
                        <Button variant="primary" disabled={processing} onClick={finish}>
                            {t('voice__done', { ns: 'common' })}
                        </Button>
                    </div>
                </div>
            </Dialog>
        );
    },
);
